import React, { useContext } from 'react';
import { useAppSelector } from '@/features';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { type Connector, type Vehicle } from '@/types/swagger';
import { Divider, Tooltip } from '@mui/material';
import CircularProgress from 'components/CircularProgress';
import { CHARGING_PERMISSION } from 'constants/ChargingPermission';
import { getClassByStatus } from 'util/getComponentIcon';
import IntlMessages from 'util/IntlMessages';

import BatteryInfo from './BateryInfo';
import ConnectorTitle from './ConnectorTitle';
import FinalSoc from './FinalSoc';
import ManualFields from './ManualFields';
import { ConnectorDetailsContainer, ConnectorInfoContainer } from './styled';
import { type TransactionValuesProps } from './TransactionValues';

type ConnectorPreparingProps = {
  connector: Connector | null;
  isLoading: boolean;
  selectedVehicle: Vehicle | null;
  setSelectedVehicle: (vehicle: Vehicle) => void;
  odometerValue: string;
  setOdometerValue: (value: string) => void;
  socLimit: number;
  setSocLimit: (value: number) => void;
  prevSocLimit: number;
  handleUpdateCharge: () => void;
  handleToggleCharge: () => Promise<void>;
} & Partial<TransactionValuesProps>;

const ConnectorPreparing: React.FC<ConnectorPreparingProps> = ({
  status,
  connector,
  isLoading,
  selectedVehicle,
  setSelectedVehicle,
  odometerValue,
  setOdometerValue,
  socLimit,
  setSocLimit,
  prevSocLimit,
  handleUpdateCharge,
  handleToggleCharge,
}) => {
  const { user } = useContext(UserSessionContext);
  const chargePoint = useAppSelector((state) =>
    connector?.charge_point ? state.chargePoints.byId[connector?.charge_point] : null
  );

  return (
    <ConnectorDetailsContainer className="flex-fill">
      <ConnectorTitle connectorName={connector?.verbose_name || '- - -'} />
      <ConnectorInfoContainer className={getClassByStatus(status as string)}>
        <BatteryInfo connector={connector} status={status} />
      </ConnectorInfoContainer>
      <div className="d-flex flex-column flex-fill">
        {!isLoading ? (
          <div className="d-flex flex-column flex-fill justify-content-end">
            {chargePoint && !chargePoint.fill_manual_fields ? null : (
              <ManualFields
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={setSelectedVehicle}
                odometerValue={odometerValue}
                setOdometerValue={setOdometerValue}
              />
            )}
            <Divider className="my-3" />
            <FinalSoc
              disabled={!user?.groups?.includes(CHARGING_PERMISSION)}
              socLimit={socLimit}
              setSocLimit={setSocLimit}
            />
            <Tooltip
              disableInteractive
              disableHoverListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
              disableTouchListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
              title={<IntlMessages id="components.connector-details.charging-permission" />}
            >
              <div>
                <Tooltip
                  disableInteractive
                  disableHoverListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                  }
                  disableTouchListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                  }
                  title={<IntlMessages id="components.connector-details.update-charge-tooltip" />}
                  placement="top"
                >
                  <div className="mb-3">
                    <Button
                      variant="secondary"
                      className="$tw-w-full"
                      disabled={
                        !user?.groups?.includes(CHARGING_PERMISSION) || socLimit === prevSocLimit
                      }
                      onClick={handleUpdateCharge}
                    >
                      Actualizar SoC Máximo
                    </Button>
                  </div>
                </Tooltip>
                <Tooltip
                  disableInteractive
                  disableHoverListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) ||
                    !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                  }
                  disableTouchListener={
                    !user?.groups?.includes(CHARGING_PERMISSION) ||
                    !(chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                  }
                  title={<IntlMessages id="components.connector-details.manual-fields-filled" />}
                >
                  <div>
                    <Button
                      className="$tw-w-full"
                      disabled={
                        !user?.groups?.includes(CHARGING_PERMISSION) ||
                        (chargePoint?.fill_manual_fields && (!selectedVehicle || !odometerValue))
                      }
                      onClick={handleToggleCharge}
                    >
                      Iniciar carga
                    </Button>
                  </div>
                </Tooltip>
              </div>
            </Tooltip>
          </div>
        ) : (
          <div className="d-flex flex-column flex-fill justify-content-end">
            <CircularProgress />
          </div>
        )}
      </div>
    </ConnectorDetailsContainer>
  );
};

export default ConnectorPreparing;
